/**
 * [PGF Draft Board Refresh v1.1 §G.1] Board style constants - frontend mirror of
 * /app/backend/models/board_style.py. Keep in sync.
 *
 * Three canonical board styles selectable per-league (commissioner default) and
 * per-user-per-league (override in User.alpha_flags.board_style_override_by_league).
 */

export const BOARD_STYLES = Object.freeze({
  PGF_STANDARD: "pgf_standard",
  TRADITIONAL: "traditional",
  WHITEBOARD: "whiteboard",
});

export const VALID_BOARD_STYLES = Object.freeze([
  BOARD_STYLES.PGF_STANDARD,
  BOARD_STYLES.TRADITIONAL,
  BOARD_STYLES.WHITEBOARD,
]);

export const DEFAULT_BOARD_STYLE = BOARD_STYLES.PGF_STANDARD;

// [PGF Draft Board Refresh v1.1 §D.2] Traditional-specific palette.
// PGF Standard uses POSITION_COLORS_FIREWORKS from StandaloneDraftBoard;
// Traditional's palette is completely different (physical board sticker
// colors). getPositionColor(position, boardStyle) is the routing helper.
export const TRADITIONAL_POSITION_COLORS = Object.freeze({
  QB:  "#C4241F",
  RB:  "#1E5AA8",
  WR:  "#111111",
  TE:  "#159B4C",
  K:   "#0F7A3C",
  DEF: "#F27C1A",
});

// [PGF Draft Board Refresh v1.1 §E.5] Whiteboard marker ink palette -
// brighter/more saturated than physical stickers (marker on white). Colors
// do NOT desaturate in night mode (§E.9 - real dry-erase ink looks the
// same under any lighting).
export const WHITEBOARD_INK = Object.freeze({
  QB:  "#B41E1E", // deep marker red - distinct from header red #D02020
  RB:  "#1A56C4", // saturated marker blue
  WR:  "#111111", // marker black
  TE:  "#1B8F3F", // marker green
  K:   "#7A2FA0", // marker purple (distinguishes from TE green)
  DEF: "#E86A15", // marker orange
});

// [§E.3] Team-name row uses saturated dry-erase red; underscore stroke shares it.
export const WHITEBOARD_HEADER_RED = "#D02020";

// Darken a hex color to ~70% brightness for sticker edge border per §D.4.
export function darken70(hex) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.floor(((n >> 16) & 0xff) * 0.7);
  const g = Math.floor(((n >> 8) & 0xff) * 0.7);
  const b = Math.floor((n & 0xff) * 0.7);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export const BOARD_STYLE_LABELS = Object.freeze({
  [BOARD_STYLES.PGF_STANDARD]: "PGF Standard",
  [BOARD_STYLES.TRADITIONAL]: "Traditional",
  [BOARD_STYLES.WHITEBOARD]: "Whiteboard",
});

export function isValidBoardStyle(value) {
  return typeof value === "string" && VALID_BOARD_STYLES.includes(value);
}

/**
 * [§F.4] Resolve which style to render for a given league + user.
 * Precedence: user override > league default > product default.
 *
 * @param {string|null} userOverride  User's per-league override (or null).
 * @param {string|null} leagueDefault Commissioner-set league default.
 * @returns {string} One of BOARD_STYLES values, guaranteed valid.
 */
export function resolveBoardStyle(userOverride, leagueDefault) {
  if (isValidBoardStyle(userOverride)) return userOverride;
  if (isValidBoardStyle(leagueDefault)) return leagueDefault;
  return DEFAULT_BOARD_STYLE;
}

/**
 * [§G.3] localStorage key format matches existing pgf_recap_dismissed:{lid}.
 */
export function boardStyleLocalStorageKey(leagueId) {
  return `pgf_board_style:${leagueId}`;
}
